import React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Calendar, Clock, Play, Trash2, Plus, Loader2, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { relativeTime } from '@/lib/utils';

const TASK_KINDS = [
  { v: 'promote', l: 'Promote (LLM contextual)' },
  { v: 'karma_farm', l: 'Karma farm (LLM organic)' },
  { v: 'boost_comment', l: 'Boost comment' },
  { v: 'reply_mention', l: 'Reply to mentions' },
  { v: 'mobile_fidelity_audit', l: 'Mobile fidelity audit' },
  { v: 'mailbox_readiness_audit', l: 'Mailbox readiness audit' },
  { v: 'warmup', l: 'Warmup (deterministic)' },
  { v: 'natural', l: 'Natural browse' },
];

export function SchedulePanel({ agentId }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['schedules', agentId],
    queryFn: () => api.get(`/agents/${agentId}/schedules`).then(r => r.data),
    refetchInterval: 15000,
  });
  const { data: presets } = useQuery({
    queryKey: ['schedule-presets'],
    queryFn: () => api.get('/agents/schedules/presets').then(r => r.data),
  });

  const deleteMut = useMutation({
    mutationFn: (id) => api.delete(`/agents/${agentId}/schedules/${id}`).then(r => r.data),
    onSuccess: () => { toast.success('Schedule removed'); qc.invalidateQueries({ queryKey: ['schedules', agentId] }); },
  });
  const patchMut = useMutation({
    mutationFn: ({ id, body }) => api.patch(`/agents/${agentId}/schedules/${id}`, body).then(r => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['schedules', agentId] }),
  });
  const createMut = useMutation({
    mutationFn: (body) => api.post(`/agents/${agentId}/schedules`, body).then(r => r.data),
    onSuccess: () => { toast.success('Schedule created'); qc.invalidateQueries({ queryKey: ['schedules', agentId] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Create failed'),
  });

  return (
    <div className="space-y-4" data-testid="schedule-panel">
      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="flex items-center justify-between border-b border-white/5 px-5 py-3">
          <div className="flex items-center gap-2 text-sm font-medium"><Calendar className="h-4 w-4" /> Active schedules</div>
          <NewScheduleDialog onCreate={createMut.mutate} pending={createMut.isPending} />
        </div>
        <div className="p-5">
          {isLoading ? <div className="text-xs text-[hsl(var(--text-3))]">Loading…</div> :
            (data?.items || []).length === 0 ? (
              <div className="text-sm text-[hsl(var(--text-3))]">No schedules yet. Pick a preset below or create a custom one.</div>
            ) : (
              <div className="divide-y divide-white/5">
                {data.items.map(s => (
                  <div key={s.id} className="flex items-center gap-3 py-3" data-testid={`schedule-row-${s.id}`}>
                    <Clock className="h-4 w-4 text-[hsl(var(--text-3))]" />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium flex items-center gap-2">
                        {s.name}
                        <span className="rounded border border-white/10 bg-[hsl(var(--surface-2))] px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider">{s.task_kind}</span>
                      </div>
                      <div className="text-xs text-[hsl(var(--text-3))] font-mono mt-0.5">
                        {s.cron_expr} · {s.timezone} · ±{s.jitter_minutes}m jitter {s.last_fired_at && `· last ${relativeTime(s.last_fired_at)}`}
                      </div>
                    </div>
                    <Switch checked={s.enabled} onCheckedChange={(v) => patchMut.mutate({ id: s.id, body: { enabled: v } })} data-testid={`schedule-toggle-${s.id}`} />
                    <Button variant="ghost" size="icon" onClick={() => deleteMut.mutate(s.id)} data-testid={`schedule-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            )}
        </div>
      </div>

      <div className="rounded-lg border border-white/5 bg-[hsl(var(--surface-1))]">
        <div className="border-b border-white/5 px-5 py-3 text-sm font-medium flex items-center gap-2"><Zap className="h-4 w-4 text-[hsl(var(--primary))]" /> Quick presets</div>
        <div className="p-5 grid gap-3 md:grid-cols-2">
          {(presets?.items || []).map(p => (
            <div key={p.slug} className="rounded-md border border-white/5 bg-[hsl(var(--surface-2))] p-4 space-y-2" data-testid={`preset-${p.slug}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="text-sm font-medium">{p.name}</div>
                <code className="rounded border border-white/10 bg-black/30 px-1.5 py-0.5 font-mono text-[10px]">{p.cron_expr}</code>
              </div>
              <p className="text-xs text-[hsl(var(--text-3))]">{p.description}</p>
              <Button size="sm" variant="secondary" disabled={createMut.isPending} onClick={() => createMut.mutate({
                name: p.name, task_kind: p.task_kind, cron_expr: p.cron_expr,
                jitter_minutes: p.jitter_minutes, enabled: true,
              })} data-testid={`apply-preset-${p.slug}`}>
                <Play className="mr-1.5 h-3 w-3" /> Apply
              </Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function NewScheduleDialog({ onCreate, pending }) {
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({
    name: '', task_kind: 'karma_farm', cron_expr: '0 */3 * * *',
    llm_model: '', instruction: '', active_hours_start: 8, active_hours_end: 23,
    timezone: 'America/New_York', jitter_minutes: 30, enabled: true,
  });
  const intelligent = ['promote','karma_farm','boost_comment','reply_mention'].includes(form.task_kind);
  const submit = () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    if (!form.cron_expr.trim()) { toast.error('Cron expression required'); return; }
    const body = { ...form };
    if (!body.llm_model) delete body.llm_model;
    onCreate(body);
    setOpen(false);
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" data-testid="new-schedule-button"><Plus className="mr-1 h-3.5 w-3.5" /> Custom schedule</Button>
      </DialogTrigger>
      <DialogContent data-testid="new-schedule-dialog">
        <DialogHeader>
          <DialogTitle>New schedule</DialogTitle>
          <DialogDescription>Cron-driven, Temporal-backed. Jitter adds randomness to each fire time to look less robotic.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Weekday promote at 2pm" data-testid="schedule-name-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Task kind</Label>
              <Select value={form.task_kind} onValueChange={(v) => setForm({ ...form, task_kind: v })}>
                <SelectTrigger data-testid="schedule-kind-select"><SelectValue /></SelectTrigger>
                <SelectContent>{TASK_KINDS.map(k => <SelectItem key={k.v} value={k.v}>{k.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Cron expression</Label>
              <Input value={form.cron_expr} onChange={(e) => setForm({ ...form, cron_expr: e.target.value })} placeholder="0 */3 * * *" className="font-mono" data-testid="schedule-cron-input" />
            </div>
          </div>
          {intelligent && (
            <div className="space-y-1.5"><Label>LLM model</Label>
              <Select value={form.llm_model || 'default'} onValueChange={(v) => setForm({ ...form, llm_model: v === 'default' ? '' : v })}>
                <SelectTrigger data-testid="schedule-llm-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="default">Use agent default</SelectItem>
                  <SelectItem value="anthropic/claude-sonnet-4.5">Claude Sonnet 4.5</SelectItem>
                  <SelectItem value="google/gemini-2.5-flash">Gemini 2.5 Flash</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5"><Label className="text-xs">Active from</Label>
              <Input type="number" min={0} max={23} value={form.active_hours_start} onChange={(e) => setForm({ ...form, active_hours_start: Number(e.target.value) })} />
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Active to</Label>
              <Input type="number" min={0} max={23} value={form.active_hours_end} onChange={(e) => setForm({ ...form, active_hours_end: Number(e.target.value) })} />
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Jitter (min)</Label>
              <Input type="number" min={0} max={180} value={form.jitter_minutes} onChange={(e) => setForm({ ...form, jitter_minutes: Number(e.target.value) })} data-testid="schedule-jitter-input" />
            </div>
          </div>
          <div className="space-y-1.5"><Label>Timezone (IANA)</Label>
            <Input value={form.timezone} onChange={(e) => setForm({ ...form, timezone: e.target.value })} placeholder="America/New_York" />
          </div>
          <div className="space-y-1.5"><Label>Instruction (optional)</Label>
            <Textarea rows={2} value={form.instruction} onChange={(e) => setForm({ ...form, instruction: e.target.value })} placeholder='e.g. "strategy=recent" for promote' />
          </div>
        </div>
        <DialogFooter>
          <Button variant="secondary" onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={submit} disabled={pending} data-testid="schedule-create-submit">
            {pending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Create schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
